'use client';

import { useMemo, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Heart, Share2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Chip } from '@/components/ui/chip';
import { formatPriceMxn } from '@/lib/utils';
import { deriveIvaBreakdown, formatIvaMessage } from '@/lib/tax';
import { useCart } from '@/lib/cart/store';
import { SizeGuideModal, type SizeGuideData } from '@/components/product/size-guide-modal';
import type { Locale } from '@/lib/i18n/routing';

interface Variant {
  id: string;
  sku: string;
  axes: Record<string, string>;
  priceMxnCents: number;
  priceMxnCentsCompare?: number | null;
  stockOnHand?: number | null;
}

interface BuyBoxProps {
  productId: string;
  productSlug: string;
  collectionName: string;
  collectionSlug: string;
  productNameEs: string;
  typeEs?: string;
  variants: Variant[];
  /** Primer publicId de media del producto (para cart preview) */
  imagePublicId?: string | null;
  /** Resumen del producto (2-3 líneas) */
  descriptionEs?: string | null;
  /** Copy editorial más largo (Accordion "Nuestra filosofía") */
  storyEs?: string | null;
  materialsEs?: string | null;
  careEs?: string | null;
  productionDays?: number | null;
  sizeGuide?: SizeGuideData | null;
}

/**
 * Buy-box PDP estilo Zara Home (docs/patterns-zh-observed.md §5):
 *  - Colección (eyebrow linkeable)
 *  - Nombre + tipo
 *  - Precio grande + IVA mensaje
 *  - Variantes como chips (primer axis determina chips; si hay múltiples axis muestra grupos)
 *  - CTA full-width "AÑADIR (MXN X + envío Y)"
 *  - Wishlist + share sólo icono
 *  - Accordions: Envío · Composición · Tallas · Nuestra filosofía
 */
export function BuyBox({
  productId,
  productSlug,
  collectionName,
  collectionSlug,
  productNameEs,
  typeEs,
  variants,
  imagePublicId,
  descriptionEs,
  storyEs,
  materialsEs,
  careEs,
  productionDays,
  sizeGuide,
}: BuyBoxProps) {
  const t = useTranslations('pdp');
  const locale = useLocale() as Locale;
  const isEn = locale === 'en-US';
  const [selected, setSelected] = useState<Variant | null>(variants[0] ?? null);
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const addToCart = useCart((s) => s.add);

  function handleAddToCart() {
    if (!selected || adding) return;
    const outOfStock = typeof selected.stockOnHand === 'number' && selected.stockOnHand === 0;
    if (outOfStock) {
      toast.error(t('outOfStock'));
      return;
    }
    setAdding(true);
    try {
      addToCart({
        variantId: selected.id,
        productId,
        productSlug,
        collectionSlug,
        productNameEs,
        axes: selected.axes,
        sku: selected.sku,
        unitPriceMxnCents: selected.priceMxnCents,
        unitPriceMxnCentsCompare: selected.priceMxnCentsCompare ?? null,
        imagePublicId: imagePublicId ?? null,
        stockAtAdd: selected.stockOnHand ?? null,
      });
      toast.success(`${productNameEs} · ${t('addedToCart')}`);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1600);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('addToCart'));
    } finally {
      setAdding(false);
    }
  }

  // Extraer los axes únicos (talla, largo, color, etc.)
  const axisKeys = useMemo(() => {
    const keys = new Set<string>();
    variants.forEach((v) => Object.keys(v.axes).forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [variants]);

  // Group variants by primary axis (first detected key)
  const primaryAxis = axisKeys[0];

  const iva = selected ? deriveIvaBreakdown(selected.priceMxnCents) : null;

  return (
    <div className="md:sticky md:top-32 md:self-start md:pl-8 lg:pl-16">
      {/* Colección */}
      <a
        href={`/colecciones/${collectionSlug}`}
        className="type-eyebrow text-[var(--color-fg-subtle)] transition-colors hover:text-[var(--color-accent)]"
      >
        {collectionName}
      </a>

      {/* Nombre */}
      <h1 className="type-h2 mt-2 text-[var(--color-fg)]">{productNameEs}</h1>
      {typeEs ? <p className="type-body-lg mt-1 text-[var(--color-fg-muted)]">{typeEs}</p> : null}

      {/* Precio */}
      <div className="mt-6">
        {selected ? (
          <p className="type-price-display text-[var(--color-fg)]">
            {formatPriceMxn(selected.priceMxnCents)}
            {selected.priceMxnCentsCompare ? (
              <span className="type-body-sm ml-3 text-[var(--color-fg-subtle)] line-through">
                {formatPriceMxn(selected.priceMxnCentsCompare)}
              </span>
            ) : null}
          </p>
        ) : null}
        {iva ? (
          <p className="type-caption mt-1 text-[var(--color-fg-subtle)]">
            {formatIvaMessage(selected!.priceMxnCents)}
          </p>
        ) : null}
        {productionDays ? (
          <p className="type-caption mt-2 text-[var(--color-fg-subtle)]">
            {isEn
              ? `Estimated shipping ${productionDays}–${productionDays + 14} days · made to order`
              : `Envío estimado ${productionDays}–${productionDays + 14} días · hecho a pedido`}
          </p>
        ) : null}
      </div>

      {/* Descripción corta */}
      {descriptionEs ? (
        <p className="type-body mt-6 text-[var(--color-fg-muted)]">{descriptionEs}</p>
      ) : null}

      {/* Variantes chips */}
      {primaryAxis && variants.length > 1 ? (
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <p className="type-eyebrow text-[var(--color-fg-subtle)]">{primaryAxis}</p>
            <button className="type-caption text-[var(--color-fg-subtle)] underline underline-offset-2 hover:text-[var(--color-accent)]">
              {t('sizeGuideTitle')}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {variants.map((v) => {
              const disabled = typeof v.stockOnHand === 'number' && v.stockOnHand === 0;
              return (
                <Chip
                  key={v.id}
                  onClick={() => setSelected(v)}
                  state={selected?.id === v.id ? 'selected' : 'default'}
                  disabled={disabled}
                >
                  {v.axes[primaryAxis] ?? v.sku.slice(-4)}
                </Chip>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* CTA full-width */}
      <div className="mt-10 flex items-stretch gap-2">
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={!selected || adding}
          className="type-nav inline-flex flex-1 items-center justify-center gap-2 rounded-sm bg-[var(--color-accent)] px-8 py-4 text-[var(--color-accent-fg)] transition-colors hover:bg-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {adding ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : justAdded ? (
            <>
              <Check className="h-4 w-4" />
              {t('addedToCart')}
            </>
          ) : (
            t('addToCart')
          )}
        </button>
        <button
          aria-label={t('wishlistAdd')}
          className="flex h-auto w-14 items-center justify-center rounded-sm border border-[var(--color-border-strong)] text-[var(--color-fg)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
        >
          <Heart className="h-5 w-5" strokeWidth={1.25} />
        </button>
        <button
          aria-label={t('shareProduct')}
          className="flex h-auto w-14 items-center justify-center rounded-sm border border-[var(--color-border-strong)] text-[var(--color-fg)] transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
        >
          <Share2 className="h-5 w-5" strokeWidth={1.25} />
        </button>
      </div>

      {/* Trust line */}
      <p className="type-caption mt-4 text-[var(--color-fg-subtle)]">
        {isEn
          ? 'Free shipping on orders over $1,499 MXN · 30 days for exchanges and returns'
          : 'Envío gratis en compras mayores a $1,499 MXN · 30 días para cambios y devoluciones'}
      </p>

      {/* Accordions */}
      <div className="mt-10">
        <Accordion type="multiple" className="w-full">
          {careEs || materialsEs ? (
            <AccordionItem value="composicion">
              <AccordionTrigger>
                {isEn ? 'Composition and care' : 'Composición y cuidados'}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3">
                  {materialsEs ? <p>{materialsEs}</p> : null}
                  {careEs ? <p className="text-[var(--color-fg-muted)]">{careEs}</p> : null}
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : null}
          <AccordionItem value="envio">
            <AccordionTrigger>
              {isEn ? 'Shipping and delivery' : 'Envío y entrega'}
            </AccordionTrigger>
            <AccordionContent>
              <ul className="type-body-sm space-y-2">
                {isEn ? (
                  <>
                    <li>• Metro CDMX: 1–2 business days · $99 (free &gt; $1,499)</li>
                    <li>• Zone 1: 2–4 days · $149</li>
                    <li>• Zone 2: 3–5 days · $179</li>
                    <li>• Zone 3: 4–7 days · $219</li>
                    <li>• Same-day CDMX (before 11h): $249</li>
                  </>
                ) : (
                  <>
                    <li>• Metro CDMX: 1–2 días hábiles · $99 (gratis &gt; $1,499)</li>
                    <li>• Zona 1: 2–4 días · $149</li>
                    <li>• Zona 2: 3–5 días · $179</li>
                    <li>• Zona 3: 4–7 días · $219</li>
                    <li>• Same-day CDMX (antes 11h): $249</li>
                  </>
                )}
              </ul>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="tallas">
            <AccordionTrigger>{isEn ? 'Sizing' : 'Tallas y medidas'}</AccordionTrigger>
            <AccordionContent>
              <p>
                {isEn
                  ? 'Each piece is handmade: measurements are approximate.'
                  : 'Cada pieza es elaborada a mano: las medidas son aproximadas.'}
              </p>
              {sizeGuide && sizeGuide.rows.length > 0 ? (
                <div className="mt-3">
                  <SizeGuideModal guide={sizeGuide} />
                </div>
              ) : null}
            </AccordionContent>
          </AccordionItem>
          {storyEs ? (
            <AccordionItem value="filosofia">
              <AccordionTrigger>{isEn ? 'Our philosophy' : 'Nuestra filosofía'}</AccordionTrigger>
              <AccordionContent>
                <p className="text-[var(--color-fg-muted)] italic">{storyEs}</p>
              </AccordionContent>
            </AccordionItem>
          ) : null}
          <AccordionItem value="devoluciones">
            <AccordionTrigger>{isEn ? 'Returns' : 'Devoluciones'}</AccordionTrigger>
            <AccordionContent>
              <p>
                {isEn
                  ? '30 calendar days from delivery. Product new with tags. Free return label if defective; $99 deducted if change of mind.'
                  : '30 días naturales desde la entrega. Producto nuevo con etiquetas. Guía de retorno gratuita si hay defecto; $99 descontados si es cambio de opinión.'}
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}
